import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild, inject } from '@angular/core';
import { NgxExtendedPdfViewerModule, pdfDefaultOptions } from 'ngx-extended-pdf-viewer';

import {
  DEFAULT_BRAND_SIGNATURE_FRAME_SETTINGS,
  type BrandSignatureFrameSettings,
} from '../../models/brand.models';
import type { DocumentFieldDraft } from '../../models/document.models';
import type { TemplateTextboxResizeType } from '../../models/template.models';

type FieldNodeKind = 'deferred' | 'mark' | 'image' | 'choice' | 'dropdown' | 'text';
type FieldNodeCacheEntry = { page: number; element: HTMLElement; kind: FieldNodeKind };

interface ReviewMetaItem {
  label: string;
  value: string;
}

interface ResolvedFieldFontSettings {
  family: string;
  size: number;
  lineHeight: number;
  bold: boolean;
  italic: boolean;
  underline: boolean;
  color: string;
  align: 'left' | 'center' | 'right';
  characterSpacing: number;
  rtl: boolean;
}

interface ResolvedTextboxSettings {
  resizeType: TemplateTextboxResizeType;
  fixedWidth: boolean;
  characterLimit?: number;
}

const DEFAULT_FONT_SETTINGS: ResolvedFieldFontSettings = {
  family: 'Helvetica',
  size: 13,
  lineHeight: 15,
  bold: false,
  italic: false,
  underline: false,
  color: '#1b2d3f',
  align: 'left',
  characterSpacing: 0,
  rtl: false,
};

const DEFAULT_TEXTBOX_SETTINGS: ResolvedTextboxSettings = {
  resizeType: 'grow_vertically',
  fixedWidth: false,
  characterLimit: undefined,
};

const TEXTBOX_HORIZONTAL_PADDING_PX = 6;
const TEXTBOX_VERTICAL_PADDING_PX = 3;
const DEFAULT_PAGE_PIXEL_WIDTH = 1000;
const DEFAULT_PAGE_PIXEL_HEIGHT = 1400;
const MIN_AUTO_RESIZE_FONT_SIZE_PX = 8;
const APPROXIMATE_CHARACTER_WIDTH_FACTOR = 0.6;
const DOCUMENT_ID_HEADER_CLASS = 'document-id-page-header';

const SUPPORTED_FIELD_TYPES: DocumentFieldDraft['type'][] = [
  'signature',
  'initials',
  'textbox',
  'date_signed',
  'checkbox',
  'radio',
  'editable_date',
  'dropdown',
  'image',
  'name',
  'email',
  'title',
  'company',
];

@Component({
  selector: 'app-read-only-pdf-review',
  imports: [CommonModule, NgxExtendedPdfViewerModule],
  templateUrl: './read-only-pdf-review.component.html',
  styleUrl: './read-only-pdf-review.component.scss',
})
export class ReadOnlyPdfReviewComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('viewerHost') private readonly viewerHost?: ElementRef<HTMLElement>;

  @Input({ required: true }) eyebrow = '';
  @Input({ required: true }) title = '';
  @Input({ required: true }) copy = '';
  @Input() metaItems: ReviewMetaItem[] = [];
  @Input() pdfUrl: string | null = null;
  @Input() documentId = '';
  @Input() fields: DocumentFieldDraft[] = [];
  @Input() fieldValues: ReadonlyMap<string, unknown> = new Map();
  @Input() brandSignatureFrame: BrandSignatureFrameSettings = DEFAULT_BRAND_SIGNATURE_FRAME_SETTINGS;
  @Input() signatureFrameMetadata: Record<
    string,
    {
      name: string;
      email: string;
      timestampLabel: string;
      documentId: string;
    }
  > = {};
  @Input() maskTextboxes = false;
  @Input() tone: 'default' | 'declined' = 'default';
  @Input() emptyText = 'Preview unavailable.';

  viewerPage = 1;
  pageCount = 1;
  zoomLevel = 'page-width';
  viewerRotation: 0 | 90 | 180 | 270 = 0;

  private readonly cdr = inject(ChangeDetectorRef);
  private fieldNodeCache = new Map<string, FieldNodeCacheEntry>();
  private overlaySyncTimer: ReturnType<typeof setTimeout> | null = null;
  private previousDisableHistory = pdfDefaultOptions.disableHistory;
  private textMeasureElement: HTMLDivElement | null = null;

  ngOnInit(): void {
    this.previousDisableHistory = pdfDefaultOptions.disableHistory;
    pdfDefaultOptions.disableHistory = true;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['pdfUrl'] && !changes['pdfUrl'].firstChange) {
      this.viewerPage = 1;
      this.viewerRotation = 0;
    }

    this.scheduleOverlaySync();
  }

  ngOnDestroy(): void {
    if (this.overlaySyncTimer) {
      clearTimeout(this.overlaySyncTimer);
      this.overlaySyncTimer = null;
    }

    this.clearFieldNodeCache();
    this.textMeasureElement?.remove();
    this.textMeasureElement = null;
    pdfDefaultOptions.disableHistory = this.previousDisableHistory;
  }

  onPagesLoaded(event: unknown): void {
    const totalPages =
      (event as { pagesCount?: unknown })?.pagesCount ??
      (event as { source?: { pagesCount?: unknown } })?.source?.pagesCount;
    if (typeof totalPages === 'number' && Number.isInteger(totalPages) && totalPages > 0) {
      this.pageCount = totalPages;
      this.viewerPage = this.clamp(this.viewerPage, 1, this.pageCount);
    }

    this.scheduleOverlaySync();
  }

  onPageChange(event: unknown): void {
    const nextPage = this.extractPageNumber(event);
    if (nextPage) {
      this.viewerPage = nextPage;
    }

    this.scheduleOverlaySync();
  }

  onPageRendered(): void {
    this.ensureViewerRotationReset();
    this.scheduleOverlaySync();
  }

  onViewerRotationChange(event: unknown): void {
    const rotation = this.extractRotation(event);
    if (rotation !== null) {
      this.viewerRotation = rotation;
    }

    this.ensureViewerRotationReset();
  }

  syncOverlays(): void {
    const host = this.viewerHost?.nativeElement;
    if (!host) {
      return;
    }

    const pages = Array.from(host.querySelectorAll<HTMLElement>('.page[data-page-number]'));
    const renderedFieldIds = new Set<string>();

    for (const pageElement of pages) {
      pageElement.style.position = 'relative';
      const pageNumber = Number(pageElement.dataset['pageNumber']);
      if (!Number.isInteger(pageNumber)) {
        continue;
      }

      const layer = this.ensureOverlayLayer(pageElement);
      this.syncDocumentHeader(layer);
      const pageFields = this.fields
        .filter((field) => field.page === pageNumber)
        .sort((left, right) => (left.rect.y === right.rect.y ? left.rect.x - right.rect.x : left.rect.y - right.rect.y));

      for (const field of pageFields) {
        const node = this.getOrCreateFieldNode(field);
        if (node.parentElement !== layer) {
          layer.append(node);
        }
        this.updateFieldNode(field, node);
        renderedFieldIds.add(field.id);
      }
    }

    this.removeStaleFieldNodes(renderedFieldIds);
    this.cdr.markForCheck();
  }

  private scheduleOverlaySync(): void {
    if (this.overlaySyncTimer) {
      clearTimeout(this.overlaySyncTimer);
    }

    this.overlaySyncTimer = setTimeout(() => this.syncOverlays(), 0);
  }

  private ensureViewerRotationReset(): void {
    if (this.viewerRotation === 0) {
      return;
    }

    this.viewerRotation = 0;
    this.cdr.markForCheck();
    this.scheduleOverlaySync();
  }

  private ensureOverlayLayer(pageElement: HTMLElement): HTMLElement {
    const existing = pageElement.querySelector<HTMLElement>(':scope > .sign-overlay-layer');
    if (existing) {
      return existing;
    }

    const layer = document.createElement('div');
    layer.className = 'sign-overlay-layer';
    pageElement.append(layer);
    return layer;
  }

  private syncDocumentHeader(layer: HTMLElement): void {
    const existing = layer.querySelector<HTMLElement>(`:scope > .${DOCUMENT_ID_HEADER_CLASS}`);
    const headerText = this.documentHeaderText();

    if (!headerText) {
      existing?.remove();
      return;
    }

    const header = existing ?? document.createElement('p');
    header.className = DOCUMENT_ID_HEADER_CLASS;
    header.textContent = headerText;
    if (!existing) {
      layer.prepend(header);
    }
  }

  private documentHeaderText(): string {
    const normalized = this.documentId.trim();
    return normalized ? `Document ID: ${normalized}` : '';
  }

  private getOrCreateFieldNode(field: DocumentFieldDraft): HTMLElement {
    const kind = this.fieldNodeKind(field);
    const cached = this.fieldNodeCache.get(field.id);
    if (cached && cached.page === field.page && cached.kind === kind) {
      return cached.element;
    }

    cached?.element.remove();
    const element = this.createFieldNode(field, kind);
    this.fieldNodeCache.set(field.id, { page: field.page, element, kind });
    return element;
  }

  private createFieldNode(field: DocumentFieldDraft, kind: FieldNodeKind): HTMLElement {
    const element = document.createElement('div');
    element.className = 'sign-field-chip is-readonly';
    element.dataset['fieldId'] = field.id;
    element.dataset['fieldKind'] = kind;

    if (kind === 'deferred') {
      const deferred = document.createElement('p');
      deferred.className = 'deferred-text';
      deferred.textContent = 'Coming next';
      element.append(deferred);
      return element;
    }

    if (kind === 'mark') {
      const input = document.createElement('input');
      input.type = 'text';
      input.readOnly = true;
      input.tabIndex = -1;
      input.setAttribute('aria-hidden', 'true');
      element.append(input);
      return element;
    }

    if (kind === 'image') {
      const prompt = document.createElement('p');
      prompt.className = 'image-placeholder';
      element.append(prompt);
      return element;
    }

    if (kind === 'choice') {
      const choiceMark = document.createElement('span');
      choiceMark.className = 'choice-mark';
      element.append(choiceMark);
      return element;
    }

    if (kind === 'dropdown') {
      const select = document.createElement('select');
      select.className = 'sign-dropdown-select';
      select.disabled = true;
      select.tabIndex = -1;
      element.append(select);
      return element;
    }

    if (field.type === 'textbox') {
      const content = document.createElement('div');
      content.className = 'sign-text-value';
      element.append(content);
      return element;
    }

    const input = document.createElement('input');
    input.type = 'text';
    input.readOnly = true;
    input.tabIndex = -1;
    element.append(input);
    return element;
  }

  private updateFieldNode(field: DocumentFieldDraft, element: HTMLElement): void {
    element.className = 'sign-field-chip is-readonly';
    element.dataset['fieldId'] = field.id;
    element.dataset['fieldKind'] = this.fieldNodeKind(field);

    if (this.isFieldFilled(field)) {
      element.classList.add('is-filled', 'has-display-value');
    }

    this.applyFieldRect(element, field.rect);
    const fontSettings = this.applyFieldFontStyles(element, field);

    if (!SUPPORTED_FIELD_TYPES.includes(field.type)) {
      element.classList.add('is-deferred');
      return;
    }

    if (field.type === 'signature' || field.type === 'initials') {
      this.updateMarkFieldNode(field, element);
      return;
    }

    if (field.type === 'image') {
      this.updateImageFieldNode(field, element);
      return;
    }

    if (field.type === 'checkbox' || field.type === 'radio') {
      this.updateChoiceFieldNode(field, element);
      return;
    }

    if (field.type === 'dropdown') {
      element.classList.add('is-dropdown');
      this.updateDropdownFieldNode(field, element);
      return;
    }

    this.updateTextFieldNode(field, element);

    if (field.type === 'textbox') {
      this.applyTextboxResizeBehavior(field, element, fontSettings);
      return;
    }

    if (field.type === 'name' || field.type === 'email') {
      this.applyIdentityFieldResizeBehavior(field, element, fontSettings);
    }
  }

  private updateMarkFieldNode(field: DocumentFieldDraft, element: HTMLElement): void {
    const current = this.valueForField(field).trim();
    const image = element.querySelector<HTMLImageElement>(':scope > .sign-image-preview');
    const promptInput = element.querySelector<HTMLInputElement>(':scope > input[aria-hidden="true"]');
    const existingFrame = element.querySelector<HTMLElement>(':scope > .signature-frame-chip');

    if (current.startsWith('data:image')) {
      if (field.type === 'signature' && this.brandSignatureFrame.enabled) {
        promptInput?.remove();
        image?.remove();
        element.classList.add('has-signature-frame-layout');
        this.renderSignatureFrameChip(element, current, field.id);
        return;
      }

      element.classList.remove('has-signature-frame-layout');
      existingFrame?.remove();
      promptInput?.remove();
      const preview = image ?? document.createElement('img');
      preview.src = current;
      preview.alt = `${field.type} value`;
      preview.className = 'sign-image-preview';
      if (!image) {
        element.append(preview);
      }
      return;
    }

    element.classList.remove('has-signature-frame-layout');
    existingFrame?.remove();
    image?.remove();

    const input = promptInput ?? document.createElement('input');
    input.type = 'text';
    input.value = '';
    input.readOnly = true;
    input.tabIndex = -1;
    input.placeholder = this.emptyFieldPrompt(field);
    input.setAttribute('aria-hidden', 'true');
    if (!promptInput) {
      element.append(input);
    }
  }

  private renderSignatureFrameChip(host: HTMLElement, imageDataUrl: string, fieldId: string): void {
    const metadata = this.signatureFrameMetadata[fieldId] ?? {
      name: '',
      email: '',
      timestampLabel: '',
      documentId: this.documentId,
    };

    const existing = host.querySelector<HTMLElement>(':scope > .signature-frame-chip');
    const frame = existing ?? document.createElement('div');
    frame.className = 'signature-frame-chip';
    if (!existing) {
      host.append(frame);
    }

    frame.replaceChildren();

    const header = document.createElement('p');
    header.className = 'signature-frame-chip__header';
    header.textContent =
      this.brandSignatureFrame.showTimestamp && metadata.timestampLabel
        ? `XlhireEsigns · ${metadata.timestampLabel}`
        : 'XlhireEsigns';
    frame.append(header);

    const box = document.createElement('div');
    box.className = 'signature-frame-chip__box';
    const body = document.createElement('div');
    body.className = 'signature-frame-chip__box-body';

    const signature = document.createElement('img');
    signature.src = imageDataUrl;
    signature.alt = 'signature value';
    signature.className = 'signature-frame-chip__image';
    body.append(signature);
    box.append(body);
    frame.append(box);

    const footer = document.createElement('div');
    footer.className = 'signature-frame-chip__footer';

    if (this.brandSignatureFrame.showRecipientName && metadata.name) {
      const name = document.createElement('p');
      name.className = 'signature-frame-chip__meta';
      name.textContent = metadata.name;
      footer.append(name);
    }

    if (this.brandSignatureFrame.showRecipientEmail && metadata.email) {
      const email = document.createElement('p');
      email.className = 'signature-frame-chip__meta';
      email.textContent = metadata.email;
      footer.append(email);
    }

    const documentId = document.createElement('p');
    documentId.className = 'signature-frame-chip__document-id';
    documentId.textContent = metadata.documentId || this.documentId;
    footer.append(documentId);
    frame.append(footer);
  }

  private updateImageFieldNode(field: DocumentFieldDraft, element: HTMLElement): void {
    element.classList.add('is-image');
    const current = this.valueForField(field).trim();
    const image = element.querySelector<HTMLImageElement>(':scope > .field-image-preview');
    const placeholder = element.querySelector<HTMLElement>(':scope > .image-placeholder');

    if (current.startsWith('data:image')) {
      placeholder?.remove();
      const preview = image ?? document.createElement('img');
      preview.src = current;
      preview.alt = 'Image value';
      preview.className = 'field-image-preview';
      if (!image) {
        element.append(preview);
      }
      return;
    }

    image?.remove();
    const prompt = placeholder ?? document.createElement('p');
    prompt.className = 'image-placeholder';
    prompt.textContent = this.emptyFieldPrompt(field);
    if (!placeholder) {
      element.append(prompt);
    }
  }

  private updateChoiceFieldNode(field: DocumentFieldDraft, element: HTMLElement): void {
    element.classList.add(field.type === 'checkbox' ? 'is-checkbox' : 'is-radio');
    const controlSize = field.type === 'checkbox' ? this.getCheckboxPixelSize(field) : this.getRadioPixelSize(field);
    if (controlSize !== null) {
      element.style.width = `${controlSize}px`;
      element.style.height = `${controlSize}px`;
    }

    const selected = this.isChoiceSelected(this.fieldValues.get(field.id));
    element.classList.toggle('is-selected-choice', selected);

    const choiceMark = element.querySelector<HTMLElement>(':scope > .choice-mark');
    if (!choiceMark) {
      return;
    }

    if (controlSize !== null) {
      choiceMark.style.width = '100%';
      choiceMark.style.height = '100%';
    } else {
      choiceMark.style.removeProperty('width');
      choiceMark.style.removeProperty('height');
    }
    choiceMark.classList.toggle('selected', selected);
  }

  private updateDropdownFieldNode(field: DocumentFieldDraft, element: HTMLElement): void {
    const select = element.querySelector<HTMLSelectElement>(':scope > .sign-dropdown-select');
    if (!select) {
      return;
    }

    const options = this.dropdownOptions(field);
    const hint = this.emptyFieldPrompt(field, this.dropdownHint(field));
    const selectedValue = this.dropdownNormalizedValue(field, this.fieldValues.get(field.id)) ?? '';
    const existingOptions = Array.from(select.options).map((option) => ({ value: option.value, label: option.textContent ?? '' }));
    const nextOptions = [{ value: '', label: hint }, ...options.map((optionValue) => ({ value: optionValue, label: optionValue }))];

    const optionsChanged =
      existingOptions.length !== nextOptions.length ||
      existingOptions.some((option, index) => option.value !== nextOptions[index]?.value || option.label !== nextOptions[index]?.label);

    if (optionsChanged) {
      select.replaceChildren();
      for (const optionConfig of nextOptions) {
        const option = document.createElement('option');
        option.value = optionConfig.value;
        option.textContent = optionConfig.label;
        select.append(option);
      }
    }

    if (select.value !== selectedValue) {
      select.value = selectedValue;
    }
  }

  private updateTextFieldNode(field: DocumentFieldDraft, element: HTMLElement): void {
    if (field.type === 'textbox') {
      const content = element.querySelector<HTMLElement>(':scope > .sign-text-value');
      if (!content) {
        return;
      }

      const value = this.displayValueForField(field);
      const prompt = this.emptyFieldPrompt(field, field.placeholder || '');
      const displayText = value || prompt;

      content.textContent = displayText;
      element.classList.toggle('is-empty-textbox', value.length === 0);
      return;
    }

    const input = element.querySelector<HTMLInputElement>(':scope > input:not([aria-hidden="true"])');
    if (!input) {
      return;
    }

    input.value = this.displayValueForField(field);
    input.readOnly = true;
    input.disabled = false;
    input.placeholder = this.emptyFieldPrompt(field, field.placeholder || '');
  }

  private applyTextboxResizeBehavior(
    field: DocumentFieldDraft,
    element: HTMLElement,
    fontSettings: ResolvedFieldFontSettings,
  ): void {
    const content = element.querySelector<HTMLElement>(':scope > .sign-text-value');
    if (!content) {
      return;
    }

    const textboxSettings = this.resolveTextboxSettings(field);
    const pageMetrics = this.pagePixelMetricsForFieldNode(element);
    const baseWidth = Math.max(field.rect.width * pageMetrics.width, 1);
    const baseHeight = Math.max(field.rect.height * pageMetrics.height, 1);
    const measurementText = this.textboxMeasurementText(field);

    content.style.whiteSpace = 'pre-wrap';
    content.style.overflowWrap = 'anywhere';

    if (textboxSettings.resizeType === 'fixed') {
      return;
    }

    if (textboxSettings.resizeType === 'grow_horizontally') {
      const measured = this.measureTextboxContent(measurementText, fontSettings, { whiteSpace: 'pre', minWidth: baseWidth });
      element.style.width = `${Math.max(baseWidth, measured.width)}px`;
      content.style.whiteSpace = 'nowrap';
      return;
    }

    if (textboxSettings.resizeType === 'grow_vertically') {
      const measured = this.measureTextboxContent(measurementText, fontSettings, {
        width: baseWidth,
        minHeight: baseHeight,
        whiteSpace: 'pre-wrap',
      });
      element.style.height = `${Math.max(baseHeight, measured.height)}px`;
      return;
    }

    if (textboxSettings.resizeType === 'grow_both') {
      const measured = this.measureTextboxContent(measurementText, fontSettings, {
        whiteSpace: 'pre-wrap',
        minWidth: baseWidth,
        minHeight: baseHeight,
      });
      element.style.width = `${Math.max(baseWidth, measured.width)}px`;
      element.style.height = `${Math.max(baseHeight, measured.height)}px`;
      return;
    }

    const autoResize = this.resolveAutoResizedFont(fontSettings, measurementText, baseWidth, baseHeight);
    element.style.fontSize = `${autoResize.size}px`;
    element.style.lineHeight = `${autoResize.lineHeight}px`;
  }

  private applyIdentityFieldResizeBehavior(
    field: DocumentFieldDraft,
    element: HTMLElement,
    fontSettings: ResolvedFieldFontSettings,
  ): void {
    const pageMetrics = this.pagePixelMetricsForFieldNode(element);
    const baseWidth = Math.max(field.rect.width * pageMetrics.width, 1);
    const pageLeft = field.rect.x * pageMetrics.width;
    const maxWidth = Math.max(pageMetrics.width - pageLeft, baseWidth);
    const measurementText = this.identityMeasurementText(field);
    const measured = this.measureTextboxContent(measurementText, fontSettings, { whiteSpace: 'pre', minWidth: baseWidth });
    element.style.width = `${Math.min(Math.max(baseWidth, measured.width), maxWidth)}px`;
  }

  private valueForField(field: DocumentFieldDraft): string {
    const value = this.fieldValues.get(field.id);
    if (Array.isArray(value)) {
      return value.join(', ');
    }

    if (typeof value === 'string') {
      return value;
    }

    if (typeof value === 'number') {
      return String(value);
    }

    if (typeof value === 'boolean') {
      return value ? 'true' : 'false';
    }

    return '';
  }

  private displayValueForField(field: DocumentFieldDraft): string {
    const rawValue = this.valueForField(field);
    if (field.type !== 'textbox' || !this.maskTextboxes || !this.isMaskedTextbox(field)) {
      return rawValue;
    }

    return rawValue.replace(/\S/g, '*');
  }

  private isMaskedTextbox(field: DocumentFieldDraft): boolean {
    if (!field.settings || typeof field.settings !== 'object') {
      return false;
    }

    const textbox = (field.settings as { textbox?: { maskValue?: unknown } }).textbox;
    return textbox?.maskValue === true;
  }

  private isFieldFilled(field: DocumentFieldDraft): boolean {
    if (field.type === 'signature' || field.type === 'initials' || field.type === 'image') {
      return this.valueForField(field).trim().startsWith('data:image');
    }

    if (field.type === 'checkbox' || field.type === 'radio') {
      return this.isChoiceSelected(this.fieldValues.get(field.id));
    }

    if (field.type === 'dropdown') {
      return this.dropdownNormalizedValue(field, this.fieldValues.get(field.id)) !== null;
    }

    return this.valueForField(field).trim().length > 0;
  }

  private fieldNodeKind(field: DocumentFieldDraft): FieldNodeKind {
    if (!SUPPORTED_FIELD_TYPES.includes(field.type)) {
      return 'deferred';
    }
    if (field.type === 'signature' || field.type === 'initials') {
      return 'mark';
    }
    if (field.type === 'image') {
      return 'image';
    }
    if (field.type === 'checkbox' || field.type === 'radio') {
      return 'choice';
    }
    if (field.type === 'dropdown') {
      return 'dropdown';
    }
    return 'text';
  }

  private applyFieldRect(element: HTMLElement, rect: DocumentFieldDraft['rect']): void {
    element.style.left = `${rect.x * 100}%`;
    element.style.top = `${rect.y * 100}%`;
    element.style.width = `${rect.width * 100}%`;
    element.style.height = `${rect.height * 100}%`;
  }

  private applyFieldFontStyles(element: HTMLElement, field: DocumentFieldDraft): ResolvedFieldFontSettings {
    const resolved = this.resolveFontSettings(field);
    const letterSpacing = field.type === 'name' || field.type === 'email' ? '' : `${resolved.characterSpacing}px`;

    element.style.fontFamily = resolved.family;
    element.style.fontSize = `${resolved.size}px`;
    element.style.lineHeight = `${resolved.lineHeight}px`;
    element.style.fontWeight = resolved.bold ? '700' : '500';
    element.style.fontStyle = resolved.italic ? 'italic' : 'normal';
    element.style.textDecoration = resolved.underline ? 'underline' : 'none';
    element.style.color = resolved.color;
    element.style.textAlign = resolved.align;
    element.style.letterSpacing = letterSpacing;
    element.style.direction = resolved.rtl ? 'rtl' : 'ltr';
    return resolved;
  }

  private resolveFontSettings(field: DocumentFieldDraft): ResolvedFieldFontSettings {
    const font = (field.settings as { font?: Record<string, unknown> } | undefined)?.font;
    if (!font || typeof font !== 'object') {
      return DEFAULT_FONT_SETTINGS;
    }

    return {
      family: typeof font['family'] === 'string' ? font['family'] : DEFAULT_FONT_SETTINGS.family,
      size: typeof font['size'] === 'number' ? font['size'] : DEFAULT_FONT_SETTINGS.size,
      lineHeight: typeof font['lineHeight'] === 'number' ? font['lineHeight'] : DEFAULT_FONT_SETTINGS.lineHeight,
      bold: font['bold'] === true,
      italic: font['italic'] === true,
      underline: font['underline'] === true,
      color: typeof font['color'] === 'string' ? font['color'] : DEFAULT_FONT_SETTINGS.color,
      align:
        font['align'] === 'center' || font['align'] === 'right' ? font['align'] : DEFAULT_FONT_SETTINGS.align,
      characterSpacing:
        field.type === 'name' || field.type === 'email'
          ? 0
          : typeof font['characterSpacing'] === 'number'
            ? font['characterSpacing']
            : DEFAULT_FONT_SETTINGS.characterSpacing,
      rtl: font['rtl'] === true,
    };
  }

  private resolveTextboxSettings(field: DocumentFieldDraft): ResolvedTextboxSettings {
    const textbox = (field.settings as { textbox?: Record<string, unknown> } | undefined)?.textbox;
    if (!textbox || typeof textbox !== 'object') {
      return DEFAULT_TEXTBOX_SETTINGS;
    }

    const resizeType = textbox['resizeType'];
    return {
      resizeType:
        resizeType === 'grow_horizontally' ||
        resizeType === 'grow_vertically' ||
        resizeType === 'grow_both' ||
        resizeType === 'fixed' ||
        resizeType === 'auto_resize_font'
          ? resizeType
          : DEFAULT_TEXTBOX_SETTINGS.resizeType,
      fixedWidth: textbox['fixedWidth'] === true,
      characterLimit: typeof textbox['characterLimit'] === 'number' ? textbox['characterLimit'] : undefined,
    };
  }

  private textboxMeasurementText(field: DocumentFieldDraft): string {
    const value = this.displayValueForField(field);
    return value || this.emptyFieldPrompt(field, field.placeholder || '') || ' ';
  }

  private identityMeasurementText(field: DocumentFieldDraft): string {
    return this.displayValueForField(field) || this.emptyFieldPrompt(field, field.placeholder || '') || ' ';
  }

  private pagePixelMetricsForFieldNode(element: HTMLElement): { width: number; height: number } {
    const pageElement = element.closest('.page') as HTMLElement | null;
    if (!pageElement) {
      return { width: DEFAULT_PAGE_PIXEL_WIDTH, height: DEFAULT_PAGE_PIXEL_HEIGHT };
    }

    const rect = pageElement.getBoundingClientRect();
    return {
      width: rect.width || pageElement.clientWidth || DEFAULT_PAGE_PIXEL_WIDTH,
      height: rect.height || pageElement.clientHeight || DEFAULT_PAGE_PIXEL_HEIGHT,
    };
  }

  private ensureTextMeasureElement(): HTMLDivElement {
    if (this.textMeasureElement) {
      return this.textMeasureElement;
    }

    const measure = document.createElement('div');
    measure.setAttribute('aria-hidden', 'true');
    measure.style.position = 'fixed';
    measure.style.left = '-99999px';
    measure.style.top = '0';
    measure.style.visibility = 'hidden';
    measure.style.pointerEvents = 'none';
    measure.style.boxSizing = 'border-box';
    measure.style.padding = `${TEXTBOX_VERTICAL_PADDING_PX}px ${TEXTBOX_HORIZONTAL_PADDING_PX}px`;
    document.body.append(measure);
    this.textMeasureElement = measure;
    return measure;
  }

  private measureTextboxContent(
    text: string,
    font: ResolvedFieldFontSettings,
    options: {
      width?: number;
      minWidth?: number;
      minHeight?: number;
      whiteSpace: 'pre' | 'pre-wrap';
    },
  ): { width: number; height: number } {
    const measure = this.ensureTextMeasureElement();
    measure.style.fontFamily = font.family;
    measure.style.fontSize = `${font.size}px`;
    measure.style.lineHeight = `${font.lineHeight}px`;
    measure.style.fontWeight = font.bold ? '700' : '500';
    measure.style.fontStyle = font.italic ? 'italic' : 'normal';
    measure.style.letterSpacing = `${font.characterSpacing}px`;
    measure.style.direction = font.rtl ? 'rtl' : 'ltr';
    measure.style.textAlign = font.align;
    measure.style.whiteSpace = options.whiteSpace;
    measure.style.overflowWrap = options.whiteSpace === 'pre-wrap' ? 'anywhere' : 'normal';
    measure.style.width = options.width ? `${options.width}px` : 'auto';
    measure.textContent = text || ' ';

    const rect = measure.getBoundingClientRect();
    const approximateWidth = this.approximateTextWidth(text, font);
    const width = Math.max(
      options.minWidth ?? 0,
      rect.width,
      measure.scrollWidth,
      options.width ?? approximateWidth,
      options.width ? 0 : approximateWidth,
    );
    const lineCount =
      options.width && options.whiteSpace === 'pre-wrap'
        ? Math.max(1, Math.ceil((approximateWidth - TEXTBOX_HORIZONTAL_PADDING_PX * 2) / Math.max(options.width - TEXTBOX_HORIZONTAL_PADDING_PX * 2, 1)))
        : Math.max(1, text.split('\n').length);
    const approximateHeight = lineCount * font.lineHeight + TEXTBOX_VERTICAL_PADDING_PX * 2;
    const height = Math.max(options.minHeight ?? 0, rect.height, measure.scrollHeight, approximateHeight);

    return { width, height };
  }

  private approximateTextWidth(text: string, font: ResolvedFieldFontSettings): number {
    const longestLineLength = text
      .split('\n')
      .reduce((longest, line) => Math.max(longest, line.length), 1);
    const letterSpacingWidth = Math.max(0, longestLineLength - 1) * Math.max(font.characterSpacing, 0);
    return longestLineLength * font.size * APPROXIMATE_CHARACTER_WIDTH_FACTOR + letterSpacingWidth + TEXTBOX_HORIZONTAL_PADDING_PX * 2;
  }

  private resolveAutoResizedFont(
    font: ResolvedFieldFontSettings,
    text: string,
    baseWidth: number,
    baseHeight: number,
  ): { size: number; lineHeight: number } {
    const ratio = font.lineHeight > 0 ? font.lineHeight / Math.max(font.size, 1) : 1;
    let size = font.size;
    let lineHeight = font.lineHeight;

    while (size > MIN_AUTO_RESIZE_FONT_SIZE_PX) {
      const measured = this.measureTextboxContent(text, { ...font, size, lineHeight }, {
        width: baseWidth,
        minHeight: baseHeight,
        whiteSpace: 'pre-wrap',
      });

      if (measured.height <= baseHeight && measured.width <= baseWidth) {
        break;
      }

      size -= 1;
      lineHeight = Math.max(Math.round(size * ratio), size);
    }

    return { size, lineHeight };
  }

  private fieldDisplayLabel(field: DocumentFieldDraft): string {
    return field.label || field.fieldKey || field.type.replaceAll('_', ' ');
  }

  private emptyFieldPrompt(field: DocumentFieldDraft, fallback = ''): string {
    const baseText = this.fieldDisplayLabel(field).trim() || fallback.trim();
    return field.required ? `${baseText}*` : baseText;
  }

  private isChoiceSelected(value: unknown): boolean {
    return value === true || value === 'true' || value === 1;
  }

  private getCheckboxPixelSize(field: DocumentFieldDraft): number | null {
    return this.getChoicePixelSizeFromSettings(field, 'checkbox');
  }

  private getRadioPixelSize(field: DocumentFieldDraft): number | null {
    return this.getChoicePixelSizeFromSettings(field, 'radio');
  }

  private getChoicePixelSizeFromSettings(field: DocumentFieldDraft, key: 'checkbox' | 'radio'): number | null {
    if (!field.settings || typeof field.settings !== 'object') {
      return null;
    }

    const choiceSettings = (field.settings as Record<string, unknown>)[key];
    if (!choiceSettings || typeof choiceSettings !== 'object') {
      return null;
    }

    const size = (choiceSettings as Record<string, unknown>)['size'];
    return typeof size === 'number' && Number.isFinite(size) && size > 0 ? size : null;
  }

  private dropdownOptions(field: DocumentFieldDraft): string[] {
    const options = (field.settings as { dropdown?: { options?: unknown } } | undefined)?.dropdown?.options;
    if (!Array.isArray(options)) {
      return [];
    }

    return options
      .map((entry) => (typeof entry === 'string' ? entry.trim() : ''))
      .filter((entry): entry is string => entry.length > 0);
  }

  private dropdownHint(field: DocumentFieldDraft): string {
    const hint = (field.settings as { dropdown?: { hintText?: unknown } } | undefined)?.dropdown?.hintText;
    if (typeof hint === 'string' && hint.trim()) {
      return hint.trim();
    }

    return field.placeholder?.trim() || 'Select';
  }

  private dropdownNormalizedValue(field: DocumentFieldDraft, value: unknown): string | null {
    if (typeof value !== 'string') {
      return null;
    }

    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    return this.dropdownOptions(field).includes(trimmed) ? trimmed : null;
  }

  private removeStaleFieldNodes(renderedFieldIds: Set<string>): void {
    for (const [fieldId, entry] of this.fieldNodeCache.entries()) {
      if (renderedFieldIds.has(fieldId)) {
        continue;
      }

      entry.element.remove();
      this.fieldNodeCache.delete(fieldId);
    }
  }

  private clearFieldNodeCache(): void {
    for (const entry of this.fieldNodeCache.values()) {
      entry.element.remove();
    }
    this.fieldNodeCache.clear();
  }

  private extractPageNumber(event: unknown): number | null {
    if (typeof event === 'number' && Number.isInteger(event)) {
      return event;
    }

    const fromCurrent = (event as { currentPageNumber?: unknown })?.currentPageNumber;
    if (typeof fromCurrent === 'number' && Number.isInteger(fromCurrent)) {
      return fromCurrent;
    }

    return null;
  }

  private extractRotation(event: unknown): 0 | 90 | 180 | 270 | null {
    if (event === 0 || event === 90 || event === 180 || event === 270) {
      return event;
    }

    const rotation = (event as { angle?: unknown })?.angle;
    return rotation === 0 || rotation === 90 || rotation === 180 || rotation === 270 ? rotation : null;
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
  }
}
